/**
 * Analytics Service
 * Centralized Firebase Analytics tracking for Chaggerheart
 *
 * All analytics events go through this service to ensure:
 * - Null safety (analytics may not be available)
 * - Consistent event naming
 * - Dev mode logging
 * - Easy testing/mocking
 */

import { logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import { getAnalyticsInstance } from '../firebase/config';

// Enable debug logging in development
const DEBUG = process.env.NODE_ENV === 'development';

/**
 * Safe wrapper for logEvent - handles null analytics gracefully
 */
const safeLogEvent = (eventName, params = {}) => {
  if (DEBUG) {
    console.log(`📊 Analytics: ${eventName}`, params);
  }

  const analytics = getAnalyticsInstance();
  if (!analytics) {
    if (DEBUG) {
      console.warn('Analytics not available yet');
    }
    return;
  }

  try {
    logEvent(analytics, eventName, params);
  } catch (error) {
    console.error('Analytics error:', error);
  }
};

/**
 * Set user ID for analytics
 */
export const setAnalyticsUserId = (userId) => {
  const analytics = getAnalyticsInstance();
  if (!analytics) return;
  try {
    setUserId(analytics, userId);
  } catch (error) {
    console.error('Analytics setUserId error:', error);
  }
};

/**
 * Set user properties for segmentation
 */
export const setAnalyticsUserProperties = (properties) => {
  const analytics = getAnalyticsInstance();
  if (!analytics) return;
  try {
    setUserProperties(analytics, properties);
  } catch (error) {
    console.error('Analytics setUserProperties error:', error);
  }
};

// ============================================
// AUTHENTICATION EVENTS
// ============================================

export const trackLogin = (method = 'google') => {
  safeLogEvent('login', { method });
};

export const trackSignUp = (method = 'google') => {
  safeLogEvent('sign_up', { method });
};

export const trackBetaAccessGranted = (source = 'whitelist') => {
  safeLogEvent('beta_access_granted', { source });
};

export const trackBetaAccessDenied = (status) => {
  safeLogEvent('beta_access_denied', { status });
};

// ============================================
// CHARACTER EVENTS
// ============================================

export const trackCharacterCreated = (characterData) => {
  safeLogEvent('character_created', {
    class: characterData.class || 'unknown',
    ancestry: characterData.ancestry || 'unknown',
    community: characterData.community || 'unknown',
  });
};

export const trackCharacterDeleted = (characterData) => {
  safeLogEvent('character_deleted', {
    class: characterData.class || 'unknown',
    level: characterData.level || 1,
  });
};

export const trackLevelUp = (characterData) => {
  safeLogEvent('level_up_completed', {
    class: characterData.class || 'unknown',
    new_level: characterData.level || 1,
  });
};

export const trackDomainCardAdded = (cardName, domain) => {
  safeLogEvent('domain_card_added', {
    card_name: cardName,
    domain: domain,
  });
};

export const trackDomainCardRemoved = (cardName, domain) => {
  safeLogEvent('domain_card_removed', {
    card_name: cardName,
    domain: domain,
  });
};

// ============================================
// DICE ROLLING EVENTS
// ============================================

export const trackDiceRolled = (rollType, result, withHopeFear = false) => {
  safeLogEvent('dice_rolled', {
    roll_type: rollType, // 'action', 'damage', 'reaction', etc.
    result: result, // 'success', 'failure', 'crit', 'hope', 'fear'
    with_hope_fear: withHopeFear,
  });
};

export const trackHopeSpent = (amount, source) => {
  safeLogEvent('hope_spent', {
    amount: amount,
    source: source, // 'ability', 'reroll', 'domain_card', etc.
  });
};

export const trackStressMarked = (amount, source) => {
  safeLogEvent('stress_marked', {
    amount: amount,
    source: source,
  });
};

export const trackArmorUsed = (slotsUsed) => {
  safeLogEvent('armor_used', {
    slots_used: slotsUsed,
  });
};

// ============================================
// DOMAIN CARD EVENTS
// ============================================

export const trackDomainCardActivated = (cardName, patternType) => {
  safeLogEvent('domain_card_activated', {
    card_name: cardName,
    pattern_type: patternType,
  });
};

export const trackDomainCardDismissed = (cardName) => {
  safeLogEvent('domain_card_dismissed', {
    card_name: cardName,
  });
};

export const trackSpellCast = (spellName, spellLevel) => {
  safeLogEvent('spell_cast', {
    spell_name: spellName,
    spell_level: spellLevel,
  });
};

// ============================================
// CAMPAIGN & SESSION EVENTS
// ============================================

export const trackCampaignCreated = (isGM = true) => {
  safeLogEvent('campaign_created', {
    is_gm: isGM,
  });
};

export const trackCampaignJoined = (viaCode = true) => {
  safeLogEvent('campaign_joined', {
    via_code: viaCode,
  });
};

export const trackSessionStarted = (campaignId, playerCount) => {
  safeLogEvent('session_started', {
    campaign_id: campaignId,
    player_count: playerCount,
  });
};

export const trackSessionEnded = (durationMinutes) => {
  safeLogEvent('session_ended', {
    duration_minutes: durationMinutes,
  });
};

export const trackSessionNotesSaved = (wordCount) => {
  safeLogEvent('session_notes_saved', {
    word_count: wordCount,
  });
};

export const trackSessionArchived = (sessionNumber) => {
  safeLogEvent('session_archived', {
    session_number: sessionNumber,
  });
};

// ============================================
// AI FEATURE EVENTS
// ============================================

export const trackAIPortraitRequested = (model) => {
  safeLogEvent('ai_portrait_requested', {
    model: model,
  });
};

export const trackAIPortraitSuccess = (model, generationTimeMs) => {
  safeLogEvent('ai_portrait_success', {
    model: model,
    generation_time_ms: generationTimeMs,
  });
};

export const trackAIPortraitFailed = (errorType) => {
  safeLogEvent('ai_portrait_failed', {
    error_type: errorType,
  });
};

export const trackAIRecipeRequested = (ingredientCount) => {
  safeLogEvent('ai_recipe_requested', {
    ingredient_count: ingredientCount,
  });
};

export const trackAIRecipeSuccess = (mealRating, cached = false) => {
  safeLogEvent('ai_recipe_success', {
    meal_rating: mealRating,
    cached: cached,
  });
};

export const trackAIRecipeFailed = (errorType) => {
  safeLogEvent('ai_recipe_failed', {
    error_type: errorType,
  });
};

export const trackAICreditsDepleted = (featureType) => {
  safeLogEvent('ai_credits_depleted', {
    feature_type: featureType, // 'portrait', 'recipe'
  });
};

// Balance Check events
export const trackBalanceCheckRequested = () => {
  safeLogEvent('balance_check_requested');
};

export const trackBalanceCheckSuccess = (cached = false) => {
  safeLogEvent('balance_check_success', { cached });
};

export const trackBalanceCheckFailed = (errorType) => {
  safeLogEvent('balance_check_failed', { error_type: errorType });
};

// ============================================
// CAMPAIGN FRAME EVENTS
// ============================================

export const trackFrameBeastFeastStarted = () => {
  safeLogEvent('frame_beast_feast_started');
};

export const trackFrameIngredientHarvested = (ingredientType) => {
  safeLogEvent('frame_ingredient_harvested', {
    ingredient_type: ingredientType,
  });
};

export const trackFrameMealCompleted = (rating) => {
  safeLogEvent('frame_meal_completed', {
    rating: rating,
  });
};

export const trackFrameCorruptionChanged = (delta, newTotal) => {
  safeLogEvent('frame_corruption_changed', {
    delta: delta,
    new_total: newTotal,
  });
};

// ============================================
// DOWNTIME & MISC EVENTS
// ============================================

export const trackDowntimeMoveUsed = (moveType) => {
  safeLogEvent('downtime_move_used', {
    move_type: moveType,
  });
};

export const trackCompanionSummoned = (companionType) => {
  safeLogEvent('companion_summoned', {
    companion_type: companionType,
  });
};

// ============================================
// PAGE VIEW TRACKING
// ============================================

export const trackScreenView = (screenName) => {
  safeLogEvent('screen_view', {
    screen_name: screenName,
  });
};

// ============================================
// ERROR TRACKING
// ============================================

export const trackError = (errorType, errorMessage) => {
  safeLogEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage?.substring(0, 100), // Truncate long messages
  });
};
